import { useState } from "react";
import { parseUsername } from "../utils/api.js";
import styles from "./SearchScreen.module.css";

const STATUS_LABEL = {
  "loading-preview": "Looking up profile…",
  "loading-history": "Importing workout history…"
};

function loadingLabel(loadingStatus, loadingDetail) {
  if (loadingStatus === "loading-history" && loadingDetail?.state === "queued" && loadingDetail.position > 0) {
    return `In queue · ${loadingDetail.position} ahead`;
  }
  return STATUS_LABEL[loadingStatus] || "Loading…";
}

const FEATURES = [
  {
    id: "muscle",
    eyebrow: "Muscle balance",
    title: "Chest, back, legs — balanced or not",
    body: "A six-point radar and a body map show exactly where your sets are going, period over period.",
    icon: (
      <>
        <polygon points="12,2.5 20,7.2 20,16.8 12,21.5 4,16.8 4,7.2" />
        <polygon points="12,7 16,9.3 16,14.7 12,17 8,14.7 8,9.3" opacity="0.5" />
      </>
    )
  },
  {
    id: "trends",
    eyebrow: "Volume trends",
    title: "Every week, every month",
    body: "Set counts and training volume charted over time, so you can see momentum instead of guessing at it.",
    icon: (
      <>
        <polyline points="3,17 9,11 13,14 21,5" />
        <polyline points="21,5 21,10" />
        <polyline points="21,5 16,5" />
      </>
    )
  },
  {
    id: "exercises",
    eyebrow: "Exercises & PRs",
    title: "What you actually do most",
    body: "Ranked by frequency, with the personal records from your imported history surfaced next to each one.",
    icon: (
      <>
        <line x1="3" y1="6" x2="14" y2="6" />
        <line x1="3" y1="12" x2="14" y2="12" />
        <line x1="3" y1="18" x2="10" y2="18" />
        <path d="M16 12.5l2.2 2.2L22 10" />
      </>
    )
  },
  {
    id: "equipment",
    eyebrow: "Equipment",
    title: "Barbell, dumbbell, cable, machine",
    body: "See what your training actually leans on, not just what you think it does.",
    icon: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5h-8.5V3.5Z" fill="currentColor" stroke="none" />
      </>
    )
  },
  {
    id: "compare",
    eyebrow: "Compare",
    title: "Put up to four profiles side by side",
    body: "Add another Hevy username — up to three — to compare muscle split, volume, and PRs head to head.",
    icon: (
      <>
        <circle cx="9.5" cy="12" r="7" />
        <circle cx="15.5" cy="12" r="7" />
      </>
    )
  }
];

const STEPS = [
  {
    n: "1",
    title: "Enter a Hevy username",
    body: "A handle, @tag, or a hevy.com profile link — we parse it either way."
  },
  {
    n: "2",
    title: "We read the public history",
    body: "Only what's already visible on the profile. Nothing private, nothing hidden."
  },
  {
    n: "3",
    title: "Get the full breakdown",
    body: "Ready in seconds, and cached so your next visit is instant."
  }
];

// Rendered only when the server supplies a contact, so the app never points at a
// removal route that does not exist. See PROFILE_OPTOUT_CONTACT.
const optOutContact = typeof window !== "undefined"
  ? window.__APP_CONFIG__?.optOutContact || null
  : null;

// Every guide, linked once from the landing page. The footer used to name two
// of them and leave the other five reachable only through the /guide hub.
const GUIDES = [
  {
    slug: "sets-per-muscle-per-week",
    title: "Sets per muscle per week",
    body: "What a weekly set count measures, and the range the research points at."
  },
  {
    slug: "training-volume",
    title: "Training volume",
    body: "Weight times reps — a good record of your own workload, a poor way to compare two people."
  },
  {
    slug: "estimated-1rm",
    title: "Estimated 1RM",
    body: "Where the formulas come from, where they diverge, and when they start to lie."
  },
  {
    slug: "training-plateau",
    title: "Spotting a plateau",
    body: "Most bad weeks are noise. What separates a real stall from a hard week."
  },
  {
    slug: "muscle-balance",
    title: "Muscle balance",
    body: "How sets get bucketed into muscle groups, and what an imbalance does and does not mean."
  },
  {
    slug: "training-consistency",
    title: "Consistency and streaks",
    body: "A streak counts weeks, not effort. What it hides, and the number that does not."
  },
  {
    slug: "compare-hevy-profiles",
    title: "Comparing profiles",
    body: "Putting up to four public profiles side by side, and reading the difference honestly."
  }
];

// Decorative, non-interactive echo of the real muscle-balance radar chart —
// oversized and bled off the hero edge so it reads as an emblem, not a
// second small chart.
function HexWatermark() {
  return (
    <svg className={styles.watermark} viewBox="0 0 600 600" aria-hidden="true" focusable="false">
      <polygon points="300,40 525.2,170 525.2,430 300,560 74.8,430 74.8,170" />
      <polygon points="300,125 451.6,212.5 451.6,387.5 300,475 148.4,387.5 148.4,212.5" />
      <polygon points="300,210 377.9,255 377.9,345 300,390 222.1,345 222.1,255" />
      <line x1="300" y1="300" x2="300" y2="40" />
      <line x1="300" y1="300" x2="525.2" y2="170" />
      <line x1="300" y1="300" x2="525.2" y2="430" />
      <line x1="300" y1="300" x2="300" y2="560" />
      <line x1="300" y1="300" x2="74.8" y2="430" />
      <line x1="300" y1="300" x2="74.8" y2="170" />
    </svg>
  );
}

export default function SearchScreen({ id, hidden, defaultUsername, onSearch, loading, loadingStatus, loadingDetail, error }) {
  const [input, setInput] = useState(defaultUsername || "");
  const [localError, setLocalError] = useState("");

  const displayError = error || localError;

  function handleSubmit(e) {
    e.preventDefault();
    setLocalError("");
    const username = parseUsername(input);
    if (!username || username.length < 2) {
      setLocalError("Enter a valid Hevy username.");
      return;
    }
    onSearch(username);
  }

  return (
    <div id={id} className={styles.root} hidden={hidden || undefined}>
      <section className={styles.hero}>
        <HexWatermark />

        <div className={styles.center}>
          <div className={styles.brand}>
            <svg className={styles.brandMark} viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 13h3v-2H4v2Zm13 0h3v-2h-3v2ZM8.5 16h7v-2h-7v2Zm0-6h7V8h-7v2Zm-1 4.5h-1A2.5 2.5 0 0 1 4 12v0a2.5 2.5 0 0 1 2.5-2.5h1v5Zm9 0h1A2.5 2.5 0 0 0 20 12v0a2.5 2.5 0 0 0-2.5-2.5h-1v5Z" />
            </svg>
            <span className={styles.brandName}>Hevy Analyzer</span>
            <a className={styles.brandLink} href="/guide">Guides</a>
          </div>

          {/* The page's only top-level heading: it was a <p>, which left the
              document with no h1 at all. */}
          <h1 className={styles.tagline}>
            Every rep, logged.<br />
            Every trend, clear.
          </h1>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <label className={styles.label} htmlFor="profile-input">
              Hevy username
            </label>

            <div className={styles.inputRow}>
              <div className={styles.inputShell}>
                <svg className={styles.inputIcon} viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4Z" />
                </svg>
                <input
                  id="profile-input"
                  className={styles.input}
                  type="text"
                  value={input}
                  onChange={(e) => { setInput(e.target.value); setLocalError(""); }}
                  placeholder="@username"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={loading}
                />
              </div>
              <button className={styles.submit} type="submit" disabled={loading || !input.trim()}>
                {loading ? loadingLabel(loadingStatus, loadingDetail) : "Analyze"}
              </button>
            </div>

            {displayError && (
              <p id="search-error" className={styles.error} role="alert">{displayError}</p>
            )}
          </form>

          <div className={styles.trust}>
            <span>No login required</span>
            <span className={styles.dot} aria-hidden="true">·</span>
            <span>Public profiles only</span>
            <span className={styles.dot} aria-hidden="true">·</span>
            <span>Compare up to four</span>
          </div>
        </div>

        <a className={styles.scrollCue} href="#features" aria-label="See what you get">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 16.5 4.5 9l1.4-1.4L12 13.7l6.1-6.1L19.5 9z" fill="currentColor" />
          </svg>
        </a>
      </section>

      <section id="features" className={styles.section}>
        <div className={styles.sectionInner}>
          <span className={styles.eyebrow}>What you get</span>
          <h2 className={styles.sectionTitle}>One profile in, a full breakdown out.</h2>

          <div className={styles.featureGrid}>
            {FEATURES.map((f, i) => (
              <div className={styles.featureCard} key={f.id} style={{ "--i": i }}>
                <svg className={styles.featureIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {f.icon}
                </svg>
                <span className={styles.featureEyebrow}>{f.eyebrow}</span>
                <h3 className={styles.featureTitle}>{f.title}</h3>
                <p className={styles.featureBody}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <span className={styles.eyebrow}>How it works</span>
          <h2 className={styles.sectionTitle}>Three steps, no signup.</h2>

          <ol className={styles.steps}>
            {STEPS.map((s) => (
              <li className={styles.step} key={s.n}>
                <span className={styles.stepNum}>{s.n}</span>
                <div>
                  <h3 className={styles.stepTitle}>{s.title}</h3>
                  <p className={styles.stepBody}>{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="guides" className={styles.section}>
        <div className={styles.sectionInner}>
          <span className={styles.eyebrow}>Guides</span>
          <h2 className={styles.sectionTitle}>Know what each number means.</h2>

          <ul className={styles.guideList}>
            {GUIDES.map((g) => (
              <li key={g.slug}>
                <a className={styles.guideLink} href={`/guide/${g.slug}`}>
                  <span className={styles.guideTitle}>
                    {g.title}
                    <svg className={styles.guideArrow} viewBox="0 0 24 24" aria-hidden="true">
                      <path d="m9 6 6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className={styles.guideBody}>{g.body}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <footer className={styles.footer}>
        <div className={styles.footerRow}>
          <svg className={styles.footerMark} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 13h3v-2H4v2Zm13 0h3v-2h-3v2ZM8.5 16h7v-2h-7v2Zm0-6h7V8h-7v2Zm-1 4.5h-1A2.5 2.5 0 0 1 4 12v0a2.5 2.5 0 0 1 2.5-2.5h1v5Zm9 0h1A2.5 2.5 0 0 0 20 12v0a2.5 2.5 0 0 0-2.5-2.5h-1v5Z" />
          </svg>
          <span className={styles.footerName}>Hevy Analyzer</span>
          <nav className={styles.footerLinks}>
            <a href="/guide">Guides</a>
            {optOutContact && <a href={optOutContact}>Is this your profile? Ask to be removed</a>}
          </nav>
        </div>
        <p className={styles.footerNote}>An independent project — not made or operated by Hevy.</p>
      </footer>
    </div>
  );
}
